import type { ProfileCreateRequest, ProfileResponse, ProfileUpdateRequest } from '../types';
import type { RequestFn } from '../client';

export class Profiles {
  constructor(private readonly request: RequestFn) {}

  async create(accountId: string, params: ProfileCreateRequest): Promise<ProfileResponse> {
    return this.request('POST', `/account/${encodeURIComponent(accountId)}/business-profile`, {
      body: params,
    });
  }

  async retrieve(accountId: string, profileId: string): Promise<ProfileResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(accountId)}/business-profile/${encodeURIComponent(profileId)}`,
    );
  }

  async list(accountId: string): Promise<ProfileResponse[]> {
    return this.request('GET', `/account/${encodeURIComponent(accountId)}/business-profile`);
  }

  /**
   * List the business profiles the caller can see at profile scope — the
   * `ProfileAccountRead` twin of `list()` (which needs merchant-level read).
   * A shop-scoped user gets exactly their own shop back.
   *
   * `GET /account/{accountId}/profile`
   */
  async listByProfile(accountId: string): Promise<ProfileResponse[]> {
    return this.request('GET', `/account/${encodeURIComponent(accountId)}/profile`);
  }

  async update(
    accountId: string,
    profileId: string,
    params: ProfileUpdateRequest,
  ): Promise<ProfileResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/business-profile/${encodeURIComponent(profileId)}`,
      {
        body: params,
      },
    );
  }

  async delete(accountId: string, profileId: string): Promise<ProfileResponse> {
    return this.request(
      'DELETE',
      `/account/${encodeURIComponent(accountId)}/business-profile/${encodeURIComponent(profileId)}`,
    );
  }

  // --- Advanced operations (Task 4.8) ---

  /** Toggle extended card info for a profile. `POST /account/{accountId}/business-profile/{profileId}/toggle-extended-card-info` */
  async toggleExtendedCardInfo(accountId: string, profileId: string): Promise<ProfileResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/business-profile/${encodeURIComponent(profileId)}/toggle-extended-card-info`,
    );
  }

  /** Toggle connector agnostic MIT. `POST /account/{accountId}/business-profile/{profileId}/toggle-connector-agnostic-mit` */
  async toggleConnectorAgnosticMit(accountId: string, profileId: string): Promise<ProfileResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/business-profile/${encodeURIComponent(profileId)}/toggle-connector-agnostic-mit`,
    );
  }
}
